import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import CustomCalendar from '../components/CustomCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeekendKursGrevenPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    payment_method: 'online'
  });
  const [loading, setLoading] = useState(false);

  const INSTRUCTOR_ID = 'julie-greven-weekend';
  const LESSON_PRICE = 1200;

  useEffect(() => {
    fetchAllAvailability();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability();
    }
  }, [selectedDate]);

  const fetchAllAvailability = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/availability/${INSTRUCTOR_ID}?start_date=${today}`);
      const dates = response.data.map(a => new Date(a.date));
      setAvailableDates(dates);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/availability/${INSTRUCTOR_ID}?start_date=${dateStr}`);
      
      const dayAvailability = response.data.find(a => a.date === dateStr);
      if (dayAvailability) {
        setAvailableSlots(dayAvailability.time_slots || []);
      } else {
        setAvailableSlots([]);
        toast.info('No availability for this date');
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to load availability');
    }
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    if (!formData.client_name || !formData.client_email || !formData.client_phone) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        instructor_id: INSTRUCTOR_ID,
        instructor_name: 'Julie Christiansen - Weekend Kurs Greven',
        date: selectedDate.toISOString().split('T')[0],
        start_time: selectedTime,
        duration_minutes: 120,
        booking_type: 'weekend_greven',
        language: i18n.language,
        ...formData
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;

      // Create payment session
      const paymentResponse = await axios.post(`${API}/payments/create-checkout`, null, {
        params: {
          booking_id: booking.id,
          origin_url: window.location.origin
        }
      });
      
      // Redirect to Stripe
      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="booking-page">
        <button
          onClick={() => navigate(-1)}
          className="back-arrow-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="booking-header">
          <h1 className="booking-title" data-testid="page-title">{t('Weekend Course in Greven with Julie')}</h1>
          <p style={{ color: '#6b6b6b' }}>{t('Join us for intensive courses at our stable')}</p>
        </div>

        <div className="booking-two-column">
          {/* Left Column - Calendar */}
          <div className="booking-left">
            <CustomCalendar
              availableDates={availableDates}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#8b4857', textAlign: 'center', fontWeight: 500 }}>
              {t('Colored dates are available for booking')}
            </p>
          </div>

          {/* Right Column - Information and Booking Form */}
          <div className="booking-right">
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem', fontFamily: "'Cormorant Garamond', serif" }}>
                {t('About Weekend Courses in Greven')}
              </h2>
              <div style={{ color: '#6b6b6b', lineHeight: '1.7' }}>
                <p><strong>{t('Julie Christiansen - accomplished')}</strong></p>
                <br />
                <p>
                  • {t('3x World Champion')}<br />
                  • {t('3x Nordic Champion')}<br />
                  • {t('26x Danish Champion')}
                </p>
                <br />
                <p>{t('Weekend Greven intensive text')}</p>
                <br />
                <p>
                  <strong>{t('Course Details')}</strong><br />
                  • {t('Duration 2-3 days')}<br />
                  • {t('Maximum 10 riders')}<br />
                  • {t('Location')}: {t('Our stable in Greven')}<br />
                  • <strong>{t('Price per day', { price: LESSON_PRICE })}</strong>
                </p>
              </div>
            </div>

          {selectedDate && (
            <div className="form-group">
              <label className="form-label">{t('Available Times')}</label>
              {availableSlots.length === 0 ? (
                <p style={{ color: '#999' }}>No available time slots for this date</p>
              ) : (
                <div className="time-slots-grid">
                  {availableSlots.map((slot, idx) => (
                    <div
                      key={idx}
                      className={`time-slot ${
                        slot.booked ? 'booked' : selectedTime === slot.start_time ? 'selected' : ''
                      }`}
                      onClick={() => !slot.booked && setSelectedTime(slot.start_time)}
                      data-testid={`time-slot-${slot.start_time}`}
                    >
                      {slot.start_time}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

            {selectedTime && (
              <>
                <div className="section-title" style={{ marginTop: '2rem' }}>{t('Your Information')}</div>
                
                <div className="form-group">
                  <label className="form-label">{t('Full Name')}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    data-testid="client-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Email')}</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    data-testid="client-email-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Phone')}</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    data-testid="client-phone-input"
                  />
                </div>

                <button
                  className="cta-button"
                  onClick={handleBooking}
                  disabled={loading}
                  style={{ width: '100%', marginTop: '1rem', justifyContent: 'center' }}
                  data-testid="confirm-booking-button"
                >
                  {loading ? t('Processing') : t('Book & Pay Online')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekendKursGrevenPage;
